import React from 'react';
import { Eye, FolderOpen } from 'lucide-react';
import { NodeData } from '../types';
import type { WorkspaceMediaItem } from '../utils/workspaceEdit';
import { QuickEditorNode } from './nodes/QuickEditorNode';

interface EditPageProps {
  /** The data of the workspace's edit node; undefined until the first edit is made. */
  data: NodeData | undefined;
  onDataChange: (data: NodeData) => void;
  workspaceName: string;
  /** Every video of the workspace, offered in the media list. */
  videos: WorkspaceMediaItem[];
  /** Every audio file of the workspace, offered in the media list. */
  audio: WorkspaceMediaItem[];
  userId?: string;
  ownerUserId?: string;
  isViewOnly?: boolean;
  /**
   * The save status, for the right end of the bottom bar. This page covers the canvas's top
   * bar, where the status is shown otherwise; here it sits in the page, under any dialog.
   */
  status?: React.ReactNode;
}

const NO_EDIT_YET: NodeData = {};

/**
 * The workspace's Edit page: the timeline editor over the whole screen, with the
 * workspace's videos ready in its media list. It covers the node canvas, which
 * stays mounted underneath so running generations carry on.
 */
export const EditPage: React.FC<EditPageProps> = ({
  data, onDataChange, workspaceName, videos, audio, userId, ownerUserId, isViewOnly = false, status,
}) => (
  <div className="ns-theme-light fixed inset-0 z-[60] flex flex-col bg-ink text-cream" data-edit-page>
    <div className="relative flex-1 min-h-0">
      <div className="absolute inset-0">
        <QuickEditorNode
          variant="page"
          data={data ?? NO_EDIT_YET}
          onDataChange={onDataChange}
          userId={userId}
          workspaceName={workspaceName}
          ownerUserId={ownerUserId}
          isViewOnly={isViewOnly}
          inputVideos={videos}
          inputAudio={audio}
        />
      </div>
    </div>

    {/* Bottom bar: where you are. The Nodes / Edit switch is drawn over its middle by the app, in the
        same place as on the canvas, so the space for it is kept free here: the switch (42px) with the
        canvas bars' 16px margin under it and the same over it. */}
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 h-[74px] px-4 shrink-0 bg-black/30 border-t border-white/10">
      <div className="flex items-center gap-2 min-w-0 text-xs font-semibold text-cream">
        <FolderOpen size={14} className="shrink-0 text-ink-faint" aria-hidden="true" />
        <span className="truncate" title={workspaceName}>{workspaceName}</span>
        {isViewOnly && (
          <span className="ns-chip ns-chip-sm ns-chip-warn shrink-0">
            <Eye size={12} aria-hidden="true" /> View only
          </span>
        )}
      </div>
      <div className="w-[220px]" aria-hidden="true" />
      <div className="flex items-center justify-end min-w-0">{status}</div>
    </div>
  </div>
);

export default EditPage;
